import React, { forwardRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar, type DateCalendarProps } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import { PickerDay, pickerDayClasses, type PickerDayProps } from '@mui/x-date-pickers/PickerDay';
import {
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { useAppointments } from '../store/hooks';
import type { ViewMode } from '../types';

type DateCalendarChangeHandler = NonNullable<DateCalendarProps['onChange']>;

/**
 * Custom calendar header with read-only month display and right-aligned arrows
 */
const CustomCalendarHeader: React.FC<PickersCalendarHeaderProps> = ({
  currentMonth,
  onMonthChange,
}) => {
  const handlePrevious = () => {
    const newDate = new Date(currentMonth as Date);
    newDate.setMonth(newDate.getMonth() - 1);
    onMonthChange(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentMonth as Date);
    newDate.setMonth(newDate.getMonth() + 1);
    onMonthChange(newDate);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 1,
        paddingY: 0.5,
        minHeight: 36,
      }}
    >
      {/* Month/Year display - read only, not clickable */}
      <Typography
        sx={{
          fontSize: '15px',
          fontWeight: 400,
          color: '#535976',
        }}
      >
        {format(currentMonth as Date, 'MMMM yyyy')}
      </Typography>

      {/* Navigation arrows - flushed to the right */}
      <Box sx={{ display: 'flex', gap: 0 }}>
        <IconButton
          onClick={handlePrevious}
          size="small"
          sx={{
            padding: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <ChevronLeft fontSize="small" />
        </IconButton>
        <IconButton
          onClick={handleNext}
          size="small"
          sx={{
            padding: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <ChevronRight fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

/**
 * Determines if a date is within the currently viewed range
 */
const isInViewRange = (day: Date, viewMode: ViewMode, currentDate: Date): boolean => {
  const dayStart = startOfDay(day);
  const currentStart = startOfDay(currentDate);

  switch (viewMode) {
    case 'day':
      return isSameDay(dayStart, currentStart);
    case 'week': {
      const weekStart = startOfWeek(currentStart, { weekStartsOn: 0 }); // Sunday
      const weekEnd = endOfWeek(currentStart, { weekStartsOn: 0 });
      return isWithinInterval(dayStart, { start: weekStart, end: weekEnd });
    }
    case 'month': {
      const monthStart = startOfMonth(currentStart);
      const monthEnd = endOfMonth(currentStart);
      return isWithinInterval(dayStart, { start: monthStart, end: monthEnd });
    }
    default:
      return false;
  }
};

/**
 * Custom day component wrapper that provides context to the actual day component
 */
interface DayComponentWrapperProps {
  viewMode: ViewMode;
  currentViewDate: Date;
}

const createCustomDay = ({ viewMode, currentViewDate }: DayComponentWrapperProps) => {
  const CustomDayComponent = forwardRef<HTMLButtonElement, PickerDayProps>((props, ref) => {
    const { day, today: isToday, ...other } = props;

    const isInRange = isInViewRange(day as Date, viewMode, currentViewDate);

    return (
      <PickerDay
        {...other}
        ref={ref}
        day={day}
        sx={{
          // Base styling
          '--PickerDay-horizontalMargin': 0,
          borderRadius: 1,
          transition: 'all 0.2s ease',

          // Case 1: Today (highest priority - overrides everything)
          ...(isToday && {
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            '&:focus': {
              backgroundColor: 'primary.main',
            },
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              '&:focus': {
                backgroundColor: 'primary.main',
              },
            },
          }),

          // Case 2: Selected day (not today)
          ...(!isToday && {
            '&.Mui-selected': {
              backgroundColor: 'rgba(108, 0, 204, 0.12)',
              color: 'rgba(0, 0, 0, 0.87)',
              fontWeight: 400,
              '&:hover': {
                backgroundColor: 'rgba(108, 0, 204, 0.2)',
              },
              '&:focus': {
                backgroundColor: 'rgba(108, 0, 204, 0.12)',
              },
            },
          }),

          // Case 3: View range (week/month, not today)
          ...(isInRange &&
            !isToday && {
              backgroundColor: 'rgba(230, 225, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(230, 225, 255, 0.5)',
              },
            }),

          // Case 4: Regular days (default hover only)
          ...(!isToday &&
            !isInRange && {
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }),
        }}
      />
    );
  });

  CustomDayComponent.displayName = 'CustomDayComponent';
  return CustomDayComponent as React.ComponentType<PickerDayProps>;
};

/**
 * Mini calendar sidebar component for navigation and context
 * Shows current date, highlights today, and allows date navigation
 */
export const MiniCalendar: React.FC = () => {
  const { viewMode, currentDate, setCurrentDate } = useAppointments();

  const handleDateChange: DateCalendarChangeHandler = date => {
    if (date instanceof Date) setCurrentDate(date);
  };

  // Create the custom day component with the current context
  const CustomDay = useMemo(
    () => createCustomDay({ viewMode, currentViewDate: currentDate }),
    [viewMode, currentDate],
  );

  return (
    <Box
      sx={{
        px: 2,
        mt: { xs: 0, sm: 2, md: 4 },
        height: '100%',
        overflow: 'auto',
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateCalendar
          value={currentDate}
          onChange={handleDateChange}
          views={['day']}
          slots={{
            day: CustomDay,
            calendarHeader: CustomCalendarHeader,
          }}
          sx={{
            width: '100%',
            height: 'auto',
            '& .MuiDayCalendar-slideTransition': {
              minHeight: 182,
            },
            '& .MuiPickersCalendarHeader-root': {
              paddingLeft: 1,
              paddingRight: 1,
              marginBottom: 1,
            },
            '& .MuiPickersCalendarHeader-label': {
              fontSize: '15px',
              fontWeight: 400,
            },
            '& .MuiDayCalendar-header': {
              gap: 0.5,
            },
            '& .MuiDayCalendar-weekContainer': {
              gap: 0.5,
            },
            [`& .${pickerDayClasses.root}`]: {
              fontSize: '11px',
              height: 28,
              width: 28,
              borderRadius: '50%',
            },
            '& .MuiDayCalendar-weekDayLabel': {
              fontSize: '11px',
              height: 28,
              width: 28,
              fontWeight: 500,
              color: '#1A1F3C',
            },
            '& .MuiPickersArrowSwitcher-root': {
              marginLeft: 0,
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default MiniCalendar;
